import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActions,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  VisibleFilterFields,
} from '@yeshuman/sdk'
import { useEffect, useRef, useState } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { HeartPulse, Plus } from 'lucide-react'
import {
  useConditions,
  useCreateCondition,
  useUpdateCondition,
  useDeleteCondition,
  type ConditionFilters,
  type ConditionItem,
  type CreateConditionData,
  type UpdateConditionData,
} from '../hooks/use-conditions'
import { toast } from 'sonner'
import { ConditionCard } from './condition-card'
import { ConditionFormDialog } from './condition-form-dialog'
import { HealthDisclaimer } from './health-disclaimer'
import { getHealthConditionFilterDefinitions } from '../list-filters/conditions'

const conditionFilterDefinitions = getHealthConditionFilterDefinitions()

export function ConditionsPage() {
  const [filters, setFilters] = useState<ConditionFilters>({ limit: 100 })
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ConditionItem | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
  const prevRef = useRef<ConditionItem[]>([])

  const { conditions, isLoading, error, refetch } = useConditions(filters)
  const createC = useCreateCondition()
  const updateC = useUpdateCondition()
  const deleteC = useDeleteCondition()

  useEffect(() => {
    if (conditions && prevRef.current.length > 0) {
      const changed = new Set<string>()
      conditions.forEach((row) => {
        const prev = prevRef.current.find((p) => p.id === row.id)
        if (!prev || JSON.stringify(prev) !== JSON.stringify(row)) changed.add(row.id)
      })
      if (changed.size > 0) {
        setHighlighted(changed)
        window.setTimeout(() => setHighlighted(new Set()), 3000)
      }
    }
    if (conditions) prevRef.current = conditions
  }, [conditions])

  const handleAdd = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const handleEdit = (row: ConditionItem) => {
    setEditing(row)
    setFormOpen(true)
  }

  const onCreate = async (data: CreateConditionData) => {
    await createC.mutateAsync(data)
  }

  const onUpdate = async (data: UpdateConditionData) => {
    await updateC.mutateAsync(data)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteC.mutateAsync(id)
      toast.success('Condition deleted')
      setDeletingId(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  const { title, description } = getPageHeader(
    '/conditions',
    'Conditions',
    "Conditions you're managing, in your own words — self-declared, not diagnoses.",
  )

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<HeartPulse className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={handleAdd}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Track condition
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        <HealthDisclaimer />

        <VisibleFilterFields
          listId="healthConditions"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={conditionFilterDefinitions}
          ctx={undefined}
        />

        {error ? (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load conditions: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading conditions" /> : null}

        {!isLoading && !error ? (
          conditions.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <HeartPulse className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">No conditions tracked</h3>
                <p className="mb-4 text-muted-foreground">
                  If you're managing a condition, track it here to organise the health data around it.
                </p>
                <Button onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Track a condition
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {conditions.map((row) => (
                <ConditionCard
                  key={row.id}
                  condition={row}
                  onEdit={handleEdit}
                  onDelete={(id) => setDeletingId(id)}
                  isHighlighted={highlighted.has(row.id)}
                />
              ))}
            </div>
          )
        ) : null}

        <ConditionFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open)
            if (!open) setEditing(null)
          }}
          condition={editing}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />

        <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this condition?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the condition and its links to other records. You cannot undo this.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deletingId && void handleDelete(deletingId)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
